import { html } from '@inter-ikea-kompis/web-component';
import { SalesTypeCodeEnum } from '@inter-ikea-kompis/enums';
import type {
	IBundleShoppingProduct,
	IProduct,
	IShoppingProduct,
	IStoreAvailability,
	IStoreAvailabilitySalesLocation
} from '@inter-ikea-kompis/types';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Text } from '@ingka/common-styles-webc';

import type IProductRowTranslations from '../types/IProductRowTranslations.js';

export default class ProductRowUtility {
	public static getQuantityField(options: {
		shoppingProduct: IShoppingProduct | IBundleShoppingProduct;
		isCompactLayout?: boolean;
		translations: IProductRowTranslations;
	}): RenderResult {
		const { shoppingProduct, isCompactLayout, translations } = options;
		const quantity = `${shoppingProduct.quantity}`;

		return html`
			<div class="quantity">
				<p class="${Text.classnames({ body: isCompactLayout ? { s: true } : { m: true } })}">
					<span class="visuallyHidden">${translations.quantity + ', ' + quantity}</span>
					<span aria-hidden="true">${quantity}x</span>
				</p>
			</div>
		`;
	}

	public static getArticleNumber(options: {
		product: IProduct;
		isSubtle: boolean;
		showLabel?: boolean;
		translations: IProductRowTranslations;
	}): RenderResult {
		const { product, isSubtle, showLabel, translations } = options;

		const { content, itemId } = product;

		const { productArticleNo } = translations;
		let formattedItemNo: string;

		if (content?.ruItemNo) {
			formattedItemNo = ProductUtility.formatItemNo(content.ruItemNo);
		} else {
			formattedItemNo = ProductUtility.formatItemNo(itemId.split('-').reverse()[0]);
		}

		return html`
			<div class="articleNumber ${isSubtle ? 'subtle' : ''}">
				<skapa-product-identifier
					aria-hidden="true"
					label="${showLabel ? productArticleNo : ''}"
					?subtle="${isSubtle}"
				>
					${formattedItemNo}
				</skapa-product-identifier>
				${this.getVisuallyHiddenProductIdentifier({ formattedItemNo, translations })}
			</div>
		`;
	}

	public static getVisuallyHiddenProductIdentifier(options: {
		formattedItemNo: string;
		translations: IProductRowTranslations;
	}): RenderResult {
		const { translations, formattedItemNo } = options;
		const productArticleNo = translations.productArticleNo;
		return html`<span class="visuallyHidden">${productArticleNo + ' ' + formattedItemNo}</span>`;
	}

	public static getLocation(options: {
		storeAvailability: IStoreAvailability | null;
		displayStoreAvailability: boolean;
		isCompactLayout?: boolean;
		translations: IProductRowTranslations;
	}): RenderResult {
		const { storeAvailability, displayStoreAvailability, isCompactLayout, translations } = options;

		if (!storeAvailability && !displayStoreAvailability) {
			return null;
		}

		const salesLocation = storeAvailability?.content?.cashCarry?.salesLocations?.[0] || {};
		const isDepartment =
			salesLocation.type === SalesTypeCodeEnum.department && salesLocation?.departmentName;

		if (
			salesLocation.type === SalesTypeCodeEnum.aisleAndBin &&
			salesLocation.aisle &&
			salesLocation.bin
		) {
			return this.getSelfServeLocation({ salesLocation, translations, isCompactLayout });
		}

		const { productSalesLocation, contactCoWorker } = translations;
		const text = isDepartment ? salesLocation.departmentName : contactCoWorker;
		return html`
			<div class="locationContainer">
				<span class="visuallyHidden">${productSalesLocation + ' ' + text}</span>
				${!isCompactLayout
					? html`<p aria-hidden="true" class="locationLabel text--body-s">
							${productSalesLocation}
						</p>`
					: null}
				<p aria-hidden="true" class="locationText text--body-s">${text}</p>
			</div>
		`;
	}

	private static getSelfServeLocation(options: {
		salesLocation: IStoreAvailabilitySalesLocation;
		translations: IProductRowTranslations;
		isCompactLayout?: boolean;
	}): RenderResult {
		const { salesLocation, translations, isCompactLayout } = options;
		const { productSalesAisle, productSalesLocation } = translations;

		return html`
			<div class="selfServeLocationContainer">
				<span class="visuallyHidden">
					${`${productSalesAisle} ${salesLocation.aisle}. ${productSalesLocation} ${salesLocation.bin}.`}
				</span>
				<skapa-product-identifier
					aria-hidden="true"
					label="${isCompactLayout ? '' : productSalesAisle}"
				>
					${salesLocation.aisle}
				</skapa-product-identifier>

				<div class="productIdentifierDivider"></div>

				<skapa-product-identifier
					aria-hidden="true"
					label="${isCompactLayout ? '' : productSalesLocation}"
				>
					${salesLocation.bin}
				</skapa-product-identifier>
			</div>
		`;
	}
}
